/**
 * API Configuration - Single Source of Truth
 *
 * This is the ONLY place where API URLs are defined.
 * All HTTP requests MUST use the exported `getApiUrl()` function.
 *
 * DO NOT hardcode API URLs in components.
 * DO NOT use `fetch()` directly - use the `http` client from network/http.ts
 */

/**
 * Environment-specific API URLs
 * Updated 2026-01-13: Using correct custom domains (not .workers.dev)
 * Force rebuild: v2 - CDN cache invalidation
 */
const API_URLS = {
  localhost: 'http://localhost:4200/api/v1',
  development: 'https://api.dev.chamama.bodywave.org/api/v1',
  production: 'https://api.prod.chamama.bodywave.org/api/v1',
} as const;

/**
 * Get API base URL for current environment
 * @returns {string} - Base URL for API requests
 */
export function getApiUrl(): string {
  // Priority 1: Environment variable (set by Vite)
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    return envUrl;
  }

  // Priority 2: App environment mode
  const appEnv = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

  switch (appEnv) {
    case 'production':
      return API_URLS.production;
    case 'development':
      return API_URLS.development;
    default:
      return API_URLS.localhost;
  }
}

/**
 * Get Google OAuth Client ID for current environment
 */
export function getGoogleClientId(): string {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!clientId) {
    throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
  }

  return clientId;
}

/**
 * Validate API configuration on app startup
 * @throws {Error} - If configuration is invalid
 */
export function validateApiConfig(): void {
  const apiUrl = getApiUrl();

  // Validate URL format
  try {
    new URL(apiUrl);
  } catch (error) {
    throw new Error(`Invalid API URL: ${apiUrl}`);
  }

  // Warn if using localhost in production
  if (import.meta.env.PROD && apiUrl.includes('localhost')) {
    console.warn('⚠️ WARNING: Using localhost API URL in production build');
  }

  console.log('✅ API Configuration validated:', {
    apiUrl,
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    hasGoogleClientId: !!import.meta.env.VITE_GOOGLE_CLIENT_ID,
  });
}
